import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal } from 'd3-sankey';

interface SankeyNode {
  name: string;
  layer?: number;
  value?: number;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface SankeyLink {
  source: number | SankeyNode;
  target: number | SankeyNode;
  value: number;
  y0?: number;
  y1?: number;
  width?: number;
}

interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

interface SankeyDiagramProps {
  openingCash: number;
  openingBankBalance: number;
  totalBankCredits: number;
  totalBankDebits: number;
  totalExpense: number;
  closingCash: number;
  closingBankBalance: number;
  netChange: number;
  salesTotal?: number;
  purchaseTotal?: number;
}

const SankeyDiagram: React.FC<SankeyDiagramProps> = ({
  openingCash,
  openingBankBalance,
  totalBankCredits,
  totalBankDebits,
  totalExpense,
  closingCash,
  closingBankBalance,
  netChange,
  salesTotal = 0,
  purchaseTotal = 0
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Responsive breakpoints
  const getResponsiveDimensions = () => {
    if (!containerRef.current) return { width: 800, height: 400 };
    
    const containerWidth = containerRef.current.clientWidth;
    let width, height, isMobile = false;
    
    if (containerWidth < 768) {
      // Mobile
      width = Math.max(containerWidth - 20, 350);
      height = Math.max(300, width * 0.6);
      isMobile = true;
    } else if (containerWidth < 1024) {
      // Tablet
      width = Math.max(containerWidth - 40, 600);
      height = Math.max(400, width * 0.5);
    } else {
      // Desktop
      width = Math.max(containerWidth - 60, 900);
      height = Math.max(500, width * 0.45);
    }
    
    return { width, height, isMobile };
  };

  // Resize observer for responsive updates
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      const newDimensions = getResponsiveDimensions();
      setDimensions(newDimensions);
    });

    resizeObserver.observe(containerRef.current);
    
    // Set initial dimensions
    setDimensions(getResponsiveDimensions());

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return;

    // Clear previous render
    d3.select(svgRef.current).selectAll("*").remove();

    const { width, height, isMobile } = getResponsiveDimensions();
    
    // Responsive margins
    const margin = isMobile 
      ? { top: 40, right: 60, bottom: 30, left: 60 }
      : width < 1024 
        ? { top: 50, right: 80, bottom: 35, left: 80 }
        : { top: 60, right: 120, bottom: 40, left: 120 };

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    // Create tooltip
    const tooltip = d3.select("body").append("div")
      .attr("class", "sankey-tooltip")
      .style("position", "absolute")
      .style("background", "rgba(0, 0, 0, 0.8)")
      .style("color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0);

    // Define 5 clear layers with proper spacing
    const layerWidth = (width - margin.left - margin.right) / 5;
    const layerPositions = [
      margin.left,                           // Layer 0: Opening
      margin.left + layerWidth,              // Layer 1: Bank Operations
      margin.left + layerWidth * 2,          // Layer 2: Business Operations
      margin.left + layerWidth * 3,          // Layer 3: Closing
      margin.left + layerWidth * 4           // Layer 4: Net Position
    ];

    // Create nodes with correct layer positions as specified
    const sankeyData: SankeyData = {
      nodes: [
        // Layer 1: Opening Cash and Opening Bank
        { name: "Opening Cash", layer: 0 },      // 0
        { name: "Opening Bank", layer: 0 },      // 1
        
        // Layer 2: Bank Credits, Bank Debits, Expenses
        { name: "Bank Credits", layer: 1 },      // 2
        { name: "Bank Debits", layer: 1 },       // 3
        { name: "Expenses", layer: 1 },          // 4
        
        // Layer 3: Sales and Purchase
        { name: "Sales", layer: 2 },             // 5
        { name: "Purchase", layer: 2 },          // 6
        
        // Layer 4: Closing Cash and Closing Bank
        { name: "Closing Cash", layer: 3 },      // 7
        { name: "Closing Bank", layer: 3 },      // 8
        
        // Layer 5: Net Position Change
        { name: "Net Position Change", layer: 4 } // 9
      ],
      links: [
        // Layer 1 to Layer 2 (Opening flows to Bank Operations)
        { source: 0, target: 2, value: openingCash },        // Opening Cash to Bank Credits
        { source: 1, target: 2, value: openingBankBalance }, // Opening Bank to Bank Credits

        // Layer 2 to Layer 3 (Bank Operations to Sales/Purchase)
        { source: 2, target: 5, value: salesTotal },         // Bank Credits to Sales
        { source: 3, target: 6, value: totalBankDebits },    // Bank Debits to Purchase
        { source: 4, target: 6, value: totalExpense },       // Expenses to Purchase

        // Layer 3 to Layer 4 (Sales/Purchase to Closing)
        { source: 5, target: 7, value: salesTotal },         // Sales to Closing Cash
        { source: 6, target: 8, value: purchaseTotal },      // Purchase to Closing Bank

        // Layer 4 to Layer 5 (Closing to Net Position)
        { source: 7, target: 9, value: closingCash },
        { source: 8, target: 9, value: closingBankBalance }
      ]
    };

    // Responsive node sizing
    const nodeWidth = isMobile ? 25 : width < 1024 ? 30 : 35;
    const nodeHeight = isMobile 
      ? Math.max(40, height / 10) 
      : Math.max(70, height / 8);
    const nodeSpacing = isMobile 
      ? Math.max(15, height / 25) 
      : Math.max(25, height / 20);
    
    // Group nodes by layer
    const layerNodes: { [key: number]: any[] } = {};
    sankeyData.nodes.forEach((node, index) => {
      const layer = node.layer || 0;
      if (!layerNodes[layer]) layerNodes[layer] = [];
      layerNodes[layer].push({ ...node, index });
    });

    // Manually position all nodes
    const positionedNodes: any[] = [];
    Object.keys(layerNodes).forEach(layerKey => {
      const layer = parseInt(layerKey);
      const layerNodeList = layerNodes[layer];
      const availableHeight = height - margin.top - margin.bottom;
      const totalNodesHeight = layerNodeList.length * nodeHeight + (layerNodeList.length - 1) * nodeSpacing;
      const startY = margin.top + (availableHeight - totalNodesHeight) / 2;

      layerNodeList.forEach((node, index) => {
        const positionedNode = {
          ...node,
          x0: layerPositions[layer],
          x1: layerPositions[layer] + nodeWidth,
          y0: startY + index * (nodeHeight + nodeSpacing),
          y1: startY + index * (nodeHeight + nodeSpacing) + nodeHeight
        };
        positionedNodes[node.index] = positionedNode;
      });
    });

    // Create positioned links
    const positionedLinks = sankeyData.links.map(link => {
      const sourceNode = positionedNodes[typeof link.source === 'number' ? link.source : 0];
      const targetNode = positionedNodes[typeof link.target === 'number' ? link.target : 0];
      
      return {
        source: sourceNode,
        target: targetNode,
        value: link.value,
        // Calculate link positioning
        y0: sourceNode.y0 + sourceNode.y1 ? (sourceNode.y0 + sourceNode.y1) / 2 - 10 : sourceNode.y0,
        y1: sourceNode.y0 + sourceNode.y1 ? (sourceNode.y0 + sourceNode.y1) / 2 + 10 : sourceNode.y0 + 20,
        width: Math.max(4, Math.min(40, Math.sqrt(link.value / 1000) * 8))
      };
    });

    const nodes = positionedNodes;
    const links = positionedLinks;

    // Add responsive layer headers
    const layerHeaders = isMobile 
      ? ["Opening", "Bank Ops", "Sales/Purch", "Closing", "Net"]
      : ["Opening", "Bank Operations", "Sales/Purchase", "Closing", "Net Position"];
    
    svg.append("g")
      .selectAll("text")
      .data(layerHeaders)
      .join("text")
      .attr("x", (d, i) => layerPositions[i] + nodeWidth/2)
      .attr("y", margin.top - (isMobile ? 10 : 15))
      .attr("text-anchor", "middle")
      .style("font-size", isMobile ? "11px" : width < 1024 ? "12px" : "14px")
      .style("font-weight", "bold")
      .style("fill", "#555")
      .text(d => d);

    // Color schemes
    const colorNode = (name: string) => {
      switch (name) {
        case "Opening Cash": return "#4CAF50";
        case "Opening Bank": return "#2196F3";
        case "Bank Credits": return "#8BC34A";
        case "Bank Debits": return "#FF5722";
        case "Expenses": return "#F44336";
        case "Sales": return "#90CAF9";
        case "Purchase": return "#F06292";
        case "Closing Cash": return "#FF9800";
        case "Closing Bank": return "#9C27B0";
        case "Net Position Change": return "#66BB6A";
        default: return "#999";
      }
    };

    const colorLink = (link: any) => {
      const sourceName = link.source.name;
      const targetName = link.target.name;
      
      // More vibrant and colorful flow types
      if (sourceName.includes("Opening")) return "#2196F3"; // Bright blue for opening flows
      if (sourceName === "Bank Credits") return "#4CAF50"; // Bright green for credits
      if (sourceName === "Bank Debits") return "#FF5722"; // Bright red-orange for debits
      if (sourceName === "Expenses") return "#F44336"; // Bright red for expenses
      if (sourceName === "Sales") return "#9C27B0"; // Bright purple for sales
      if (sourceName === "Purchase") return "#E91E63"; // Bright pink for purchase
      if (targetName === "Net Position Change") return "#FF9800"; // Bright orange for final flows
      
      return "#607D8B"; // Darker gray default
    };

    // Create gradients for more attractive links
    const defs = svg.append("defs");
    
    links.forEach((link: any, index: number) => {
      const gradient = defs.append("linearGradient")
        .attr("id", `gradient-${index}`)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", link.source.x1)
        .attr("y1", (link.source.y0 + link.source.y1) / 2)
        .attr("x2", link.target.x0)
        .attr("y2", (link.target.y0 + link.target.y1) / 2);

      const linkColor = colorLink(link);
      gradient.append("stop")
        .attr("offset", "0%")
        .attr("stop-color", linkColor)
        .attr("stop-opacity", 0.9);
      
      gradient.append("stop")
        .attr("offset", "100%")
        .attr("stop-color", linkColor)
        .attr("stop-opacity", 0.6);
    });

    // Draw enhanced curved links
    svg.append("g")
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("d", (d: any) => {
        // Create more pronounced curved line from source to target
        const sourceX = d.source.x1;
        const sourceY = (d.source.y0 + d.source.y1) / 2;
        const targetX = d.target.x0;
        const targetY = (d.target.y0 + d.target.y1) / 2;
        
        // Enhanced curve with more pronounced bend
        const controlPoint1X = sourceX + (targetX - sourceX) * 0.6;
        const controlPoint2X = sourceX + (targetX - sourceX) * 0.4;
        
        return `M${sourceX},${sourceY} C${controlPoint1X},${sourceY} ${controlPoint2X},${targetY} ${targetX},${targetY}`;
      })
      .attr("stroke", (d: any, i: number) => `url(#gradient-${i})`)
      .attr("stroke-width", (d: any) => d.width || Math.max(6, Math.min(45, Math.sqrt(d.value / 1000) * 10)))
      .attr("fill", "none")
      .attr("opacity", 0.85)
      .style("filter", "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))")
      .style("transition", "all 0.3s ease")
      .on("mouseover", (event: any, d: any) => {
        // Enhance the hovered link
        d3.select(event.currentTarget)
          .attr("opacity", 1)
          .attr("stroke-width", (d.width || Math.max(6, Math.min(45, Math.sqrt(d.value / 1000) * 10))) + 4)
          .style("filter", "drop-shadow(3px 3px 6px rgba(0,0,0,0.2))");
          
        tooltip.transition().duration(200).style("opacity", 1);
        tooltip.html(`
          <div style="text-align: center;">
            <strong>${d.source.name} → ${d.target.name}</strong><br>
            <span style="font-size: 14px; color: #4CAF50;">₹${d.value.toLocaleString()}</span>
          </div>
        `)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 20) + "px");
      })
      .on("mouseout", (event: any, d: any) => {
        // Reset the link styling
        d3.select(event.currentTarget)
          .attr("opacity", 0.85)
          .attr("stroke-width", d.width || Math.max(6, Math.min(45, Math.sqrt(d.value / 1000) * 10)))
          .style("filter", "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))");
          
        tooltip.transition().duration(300).style("opacity", 0);
      });

    // Draw nodes
    const nodeGroups = svg.append("g")
      .selectAll("g")
      .data(nodes)
      .join("g");

    nodeGroups.append("rect")
      .attr("x", (d: any) => d.x0)
      .attr("y", (d: any) => d.y0)
      .attr("height", (d: any) => d.y1 - d.y0)
      .attr("width", (d: any) => d.x1 - d.x0)
      .attr("fill", (d: any) => colorNode(d.name))
      .attr("stroke", "#fff")
      .attr("stroke-width", 2)
      .attr("rx", 4);

    // Responsive node labels
    const label = nodeGroups.append("text")
      .attr("x", (d: any) => {
        const layer = (d as any).layer;
        const offset = isMobile ? 4 : 8;
        if (layer === 0) return d.x1 + offset; // Right of opening nodes
        if (layer === 4) return d.x0 - offset; // Left of net position
        return d.x0 + (isMobile ? 6 : 12); // Center of middle layers
      })
      .attr("y", (d: any) => (d.y1 + d.y0) / 2 - (isMobile ? 4 : 8))
      .attr("text-anchor", (d: any) => {
        const layer = (d as any).layer;
        if (layer === 0) return "start";
        if (layer === 4) return "end";
        return "start";
      })
      .style("font-size", isMobile ? "10px" : width < 1024 ? "11px" : "12px")
      .style("font-weight", "bold");

    label.append("tspan")
      .text((d: any) => d.name);

    label.append("tspan")
      .attr("x", (d: any) => {
        const layer = (d as any).layer;
        const offset = isMobile ? 4 : 8;
        if (layer === 0) return d.x1 + offset;
        if (layer === 4) return d.x0 - offset;
        return d.x0 + (isMobile ? 6 : 12);
      })
      .attr("dy", isMobile ? 12 : 16)
      .style("font-size", isMobile ? "9px" : width < 1024 ? "10px" : "11px")
      .style("font-weight", "normal")
      .style("fill", "#333")
      .text((d: any) => {
        // Get the actual value for each node
        let value = 0;
        switch (d.name) {
          case "Opening Cash": value = openingCash; break;
          case "Opening Bank": value = openingBankBalance; break;
          case "Bank Credits": value = totalBankCredits; break;
          case "Bank Debits": value = totalBankDebits; break;
          case "Expenses": value = totalExpense; break;
          case "Sales": value = salesTotal; break;
          case "Purchase": value = purchaseTotal; break;
          case "Closing Cash": value = closingCash; break;
          case "Closing Bank": value = closingBankBalance; break;
          case "Net Position Change": value = netChange; break;
        }
        // Format value based on screen size
        if (isMobile && value > 999) {
          return `₹${(value/1000).toFixed(0)}k`;
        }
        return `₹${value.toLocaleString()}`;
      });

    // Add layer separation lines
    for (let i = 1; i < layerPositions.length; i++) {
      svg.append("line")
        .attr("x1", layerPositions[i] - layerWidth / 2)
        .attr("y1", margin.top + 10)
        .attr("x2", layerPositions[i] - layerWidth / 2)
        .attr("y2", height - margin.bottom - 10)
        .attr("stroke", "#e0e0e0")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,5")
        .attr("opacity", 0.5);
    }

    // Cleanup tooltip on unmount
    return () => {
      d3.selectAll(".sankey-tooltip").remove();
    };
  }, [openingCash, openingBankBalance, totalBankCredits, totalBankDebits, totalExpense, closingCash, closingBankBalance, netChange, salesTotal, purchaseTotal, dimensions]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: '100%', 
        minHeight: dimensions.width < 768 ? '300px' : '500px',
        textAlign: 'center',
        overflow: 'hidden',
        padding: '0'
      }}
    >
      <svg 
        ref={svgRef}
        style={{ 
          width: '100%', 
          height: 'auto',
          display: 'block',
          maxWidth: '100%'
        }}
      ></svg>
    </div>
  );
};

export default SankeyDiagram;
 